import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VStack,
  FormControl,
  FormErrorMessage,
  Button,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { 
  CompanyInformationFormData,
  companyInformationSchema 
} from "../../schemas/recruitment-consultation.schema";
import { COMPANY_SIZE_OPTIONS } from "../../types/recruitment-consultation.types";
import { ThemedSelect } from "../../../homeowners/hire/themed-select";
import { useRecruitmentConsultation } from "../../context/recruitment-consultation.context";

interface CompanyInformationStepProps {
  onStepComplete: (data: CompanyInformationFormData) => void;
  isLoading?: boolean;
}

export const CompanyInformationStep: React.FC<CompanyInformationStepProps> = ({
  onStepComplete,
  isLoading = false,
}) => {
  const { getCompanyInfo } = useRecruitmentConsultation();
  const existingData = getCompanyInfo();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyInformationFormData>({
    resolver: zodResolver(companyInformationSchema),
    defaultValues: existingData,
  });

  const onSubmit = (data: CompanyInformationFormData) => {
    onStepComplete(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.organization}>
          <ThemedFormLabel>Organization Name</ThemedFormLabel>
          <ThemedInput
            {...register("organization")}
            placeholder="Enter your organization name"
          />
          <FormErrorMessage>{errors.organization?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.roleInOrganization}>
          <ThemedFormLabel>Your Role in Organization</ThemedFormLabel>
          <ThemedInput
            {...register("roleInOrganization")}
            placeholder="e.g., HR Manager, CEO, Recruiter"
          />
          <FormErrorMessage>
            {errors.roleInOrganization?.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.industry}>
          <ThemedFormLabel>Industry</ThemedFormLabel>
          <ThemedInput
            {...register("industry")}
            placeholder="e.g., Technology, Healthcare, Finance"
          />
          <FormErrorMessage>{errors.industry?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.companySize}>
          <ThemedFormLabel>Company Size</ThemedFormLabel>
          <ThemedSelect
            {...register("companySize")}
            placeholder="Select company size"
            size="lg"
          >
            {COMPANY_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ThemedSelect>
          <FormErrorMessage>{errors.companySize?.message}</FormErrorMessage>
        </FormControl>

        <Button
          type="submit"
          size="lg"
          w="full"
          colorScheme="flatteredFlamingo"
          rightIcon={<ArrowForwardIcon />}
          isLoading={isLoading}
          loadingText="Processing..."
          mt={4}
        >
          Next
        </Button>
      </VStack>
    </form>
  );
};
